import React, { useState, useEffect } from 'react';
import { Box, Text, useColorModeValue } from '@chakra-ui/react';
import {
  Table,
  TableHead,
  TableHeaderSortCell,
  TableSortDirection,
  TableBody,
  TableRow,
  TableCell,
  useNexus,
  Tag,
} from '@spotoninc/nexus-react';
import {
  ChevronDownIcon,
  ChevronRightIcon,
  SalesIcon,
  SendAndPayIcon,
} from '@spotoninc/nexus-web-assets';
import '../../styles/nexus-table.css';

const parseValue = value => {
  if (typeof value === 'number') return value;
  if (typeof value === 'string') {
    return parseFloat(value.replace(/[^0-9.-]+/g, '')) || 0;
  }
  return 0;
};

const parsePercentage = value => {
  if (typeof value === 'number') return value;
  if (typeof value === 'string') {
    return parseFloat(value.replace(/[^0-9.-]+/g, '')) || 0;
  }
  return 0;
};

const formatCurrency = value => {
  const numValue = parseValue(value);
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(numValue);
};

const formatPercentage = value => {
  const numValue = parsePercentage(value);
  return new Intl.NumberFormat('en-US', {
    style: 'decimal',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    useGrouping: false,
  }).format(numValue);
};

const transformData = data => {
  const sections = {};
  const nestedItems = {}; // Store items that need to be nested under parent items

  // First pass: create sections and collect items
  for (let i = 0; i < data.length; i++) {
    // Extract data from the row
    const row = data[i];
    const [
      name,
      currentYear,
      previousYear,
      change,
      changePercent,
      classification,
      parentSection, // Parent section name
      account_key, // Account key for transaction details
      level = 0, // Nesting level (0 for top-level sections, 1+ for nested items)
      index_ui, // Index UI for maintaining hierarchy
    ] = row;

    // If this is a section header (no parent section and level is 0)
    if (level === 0) {
      // Create a new section
      let sectionClassification = classification || '';

      // If no classification but name suggests a type, set a default
      if (!sectionClassification) {
        if (/revenue|income|sales/i.test(name)) {
          sectionClassification = 'Revenue';
        } else if (/expense|cost|expenditure/i.test(name)) {
          sectionClassification = 'Expense';
        }
      }

      sections[name] = {
        title: name,
        currentYear: formatCurrency(currentYear),
        previousYear: formatCurrency(previousYear),
        change: formatCurrency(change),
        changePercent: parseFloat(formatPercentage(changePercent)),
        classification: sectionClassification,
        items: [],
        _currentYear: parseValue(currentYear),
        _previousYear: parseValue(previousYear),
        _change: parseValue(change),
        account_key: account_key || name,
        index_ui: index_ui,
      };
    }
    // If this is a direct child of a section (level 1)
    else if (level === 1 && parentSection && sections[parentSection]) {
      // Create the item with all necessary properties
      const item = {
        name,
        currentYear: formatCurrency(currentYear),
        previousYear: formatCurrency(previousYear),
        change: formatCurrency(change),
        changePercent: parseFloat(formatPercentage(changePercent)),
        classification:
          classification || sections[parentSection].classification || '',
        account_key: account_key || name,
        _currentYear: parseValue(currentYear),
        _previousYear: parseValue(previousYear),
        _change: parseValue(change),
        index_ui: index_ui,
        items: [], // Add items array for potential children
      };

      // Add the item to its parent section
      sections[parentSection].items.push(item);

      // Store reference to this item for potential children
      nestedItems[name] = item;
    }
    // If this is a nested child (level > 1)
    else if (level > 1 && parentSection) {
      // Create the nested item
      const nestedItem = {
        name,
        currentYear: formatCurrency(currentYear),
        previousYear: formatCurrency(previousYear),
        change: formatCurrency(change),
        changePercent: parseFloat(formatPercentage(changePercent)),
        classification: classification || '',
        account_key: account_key || name,
        _currentYear: parseValue(currentYear),
        _previousYear: parseValue(previousYear),
        _change: parseValue(change),
        index_ui: index_ui,
        items: [], // For potential deeper nesting
      };

      // Find the parent item to nest under
      if (nestedItems[parentSection]) {
        nestedItems[parentSection].items.push(nestedItem);
        // Store reference to this item for potential children
        nestedItems[name] = nestedItem;
      } else {
        // If parent not found, try to find it in the sections
        let found = false;
        Object.keys(sections).forEach(sectionName => {
          const section = sections[sectionName];
          // Look for the parent in this section's items
          for (let j = 0; j < section.items.length; j++) {
            if (section.items[j].name === parentSection) {
              if (!section.items[j].items) {
                section.items[j].items = [];
              }
              section.items[j].items.push(nestedItem);
              nestedItems[name] = nestedItem;
              found = true;
              break;
            }
          }
        });

        // If still not found, add to the last section as a fallback
        if (!found && Object.keys(sections).length > 0) {
          const lastSection =
            sections[Object.keys(sections)[Object.keys(sections).length - 1]];
          lastSection.items.push(nestedItem);
          nestedItems[name] = nestedItem;
        }
      }
    }
  }

  // Recursive function to calculate sums for nested items
  const calculateNestedSums = items => {
    if (!items || !items.length)
      return { currentYear: 0, previousYear: 0, change: 0 };

    return items.reduce(
      (acc, item) => {
        // First calculate sums for this item's children if any
        const childSums = calculateNestedSums(item.items);

        // Add this item's values
        acc.currentYear += item._currentYear + childSums.currentYear;
        acc.previousYear += item._previousYear + childSums.previousYear;
        acc.change += item._change + childSums.change;

        // Update this item's values to include its children's values
        item._currentYear += childSums.currentYear;
        item._previousYear += childSums.previousYear;
        item._change += childSums.change;

        // Update formatted values
        item.currentYear = formatCurrency(item._currentYear);
        item.previousYear = formatCurrency(item._previousYear);
        item.change = formatCurrency(item._change);

        // Recalculate change percentage
        const changePercent =
          item._previousYear !== 0
            ? ((item._currentYear - item._previousYear) /
                Math.abs(item._previousYear)) *
              100
            : 0;
        item.changePercent = parseFloat(formatPercentage(changePercent));

        return acc;
      },
      { currentYear: 0, previousYear: 0, change: 0 }
    );
  };

  // Second pass: calculate totals for each section including all nested items
  Object.keys(sections).forEach(sectionName => {
    const section = sections[sectionName];

    // Calculate sums for all items in this section, including nested ones
    const sums = calculateNestedSums(section.items);

    // Update section values with sums
    section._currentYear = sums.currentYear;
    section._previousYear = sums.previousYear;
    section._change = sums.change;

    section.currentYear = formatCurrency(sums.currentYear);
    section.previousYear = formatCurrency(sums.previousYear);
    section.change = formatCurrency(sums.change);

    // Calculate change percentage
    const changePercent =
      sums.previousYear !== 0
        ? ((sums.currentYear - sums.previousYear) /
            Math.abs(sums.previousYear)) *
          100
        : 0;
    section.changePercent = parseFloat(formatPercentage(changePercent));
  });

  // Convert the sections object to an array
  return Object.values(sections);
};

const RecursiveTableRow = ({
  item,
  level = 0,
  expandedSections,
  toggleSection,
  setSelectedTransaction,
  highlightEntry,
  highlightedParentSection,
  fromDate,
  toDate,
  periodFrom,
  periodTo,
}) => {
  const hasItems = item.items && item.items.length > 0;
  const itemName = item.name || item.title;
  const isExpanded = expandedSections[itemName];
  const paddingLeft = level * 3;
  const isHighlighted = highlightEntry && itemName === highlightEntry;
  const isParentOfHighlighted =
    highlightedParentSection && itemName === highlightedParentSection;

  // Handle click on the row to expand/collapse
  const handleRowClick = () => {
    // Only toggle expansion if the item has children
    if (hasItems) {
      // First clear the highlight if needed, then toggle the section
      // This prevents the re-render from affecting the section toggle
      if (highlightEntry) {
        // Call the clearHighlight function passed from parent
        if (typeof setSelectedTransaction === 'function') {
          // We're using setSelectedTransaction as a way to communicate with the parent
          // Pass a special object that the parent can recognize to clear the highlight
          setSelectedTransaction({
            clearHighlight: true,
            sectionToToggle: itemName, // Pass the section name to toggle after clearing highlight
          });
        }
      } else {
        // If no highlight to clear, just toggle the section normally
        toggleSection(itemName);
      }
    }
  };

  // Handle click on the description text to navigate to transaction details
  const handleDescriptionClick = e => {
    e.stopPropagation(); // Prevent the row click from triggering

    // Only navigate to transaction details if the item has an account_key
    if (
      item.account_key &&
      item.account_key !== '-' &&
      item.account_key !== ''
    ) {
      setSelectedTransaction({
        ...item,
        account_key: item.account_key,
        fromDate,
        toDate,
        periodFrom,
        periodTo,
      });
    }
  };

  return (
    <>
      <TableRow
        className={`clickable ${isHighlighted ? 'highlighted-row' : ''}`}
        onClick={handleRowClick}
        cursor={hasItems ? 'pointer' : 'default'}
        _hover={hasItems ? { bg: 'gray.50' } : {}}
        fontWeight={isHighlighted ? 'bold' : undefined}
        bg={isParentOfHighlighted ? 'blue.50' : undefined}
        borderLeft={isParentOfHighlighted ? '4px solid #3182CE' : undefined}
        variant="bottom-divider"
      >
        <TableCell>
          <Box
            display="flex"
            alignItems="center"
            gap={2}
            pl={`${paddingLeft}rem`}
          >
            {hasItems &&
              (isExpanded ? (
                <ChevronDownIcon
                  color={isParentOfHighlighted ? '#3182CE' : undefined}
                  width={isParentOfHighlighted ? '20px' : '16px'}
                  height={isParentOfHighlighted ? '20px' : '16px'}
                />
              ) : (
                <ChevronRightIcon
                  color={isParentOfHighlighted ? '#3182CE' : undefined}
                  width={isParentOfHighlighted ? '20px' : '16px'}
                  height={isParentOfHighlighted ? '20px' : '16px'}
                />
              ))}
            {item.classification === 'Revenue' && (
              <SalesIcon width="18px" height="18px" color="#3AB6A5" />
            )}
            {item.classification === 'Expense' && (
              <SendAndPayIcon width="16px" height="16px" color="#ED5E5A" />
            )}
            <Text
              fontWeight={
                isHighlighted ? 700 : isParentOfHighlighted ? 650 : 600
              }
              color={isParentOfHighlighted ? 'blue.600' : undefined}
              fontSize={
                isHighlighted ? 'md' : isParentOfHighlighted ? 'sm' : undefined
              }
              textDecoration={isParentOfHighlighted ? 'underline' : undefined}
              onClick={handleDescriptionClick}
              cursor={
                item.account_key &&
                item.account_key !== '-' &&
                item.account_key !== ''
                  ? 'pointer'
                  : 'default'
              }
              _hover={
                item.account_key &&
                item.account_key !== '-' &&
                item.account_key !== ''
                  ? { textDecoration: 'underline', color: 'blue.500' }
                  : {}
              }
              display="inline-block"
            >
              {itemName}
            </Text>
          </Box>
        </TableCell>
        <TableCell textAlign="center">{item.currentYear || '-'}</TableCell>
        <TableCell textAlign="center">{item.previousYear || '-'}</TableCell>
        <TableCell textAlign="center">{item.change || '-'}</TableCell>
        <TableCell textAlign="center">
          {item.changePercent !== undefined && item.changePercent !== 0 ? (
            <Tag
              size="sm"
              variant={
                (item.classification === 'Revenue' && item.changePercent > 0) ||
                (item.classification === 'Expense' && item.changePercent < 0)
                  ? 'success'
                  : 'danger'
              }
            >
              {item.changePercent}%
            </Tag>
          ) : (
            '-'
          )}
        </TableCell>
      </TableRow>
      {hasItems &&
        isExpanded &&
        item.items.map((subItem, index) => (
          <RecursiveTableRow
            key={index}
            item={subItem}
            level={level + 1}
            expandedSections={expandedSections}
            toggleSection={toggleSection}
            setSelectedTransaction={setSelectedTransaction}
            highlightEntry={highlightEntry}
            highlightedParentSection={highlightedParentSection}
            fromDate={fromDate}
            toDate={toDate}
            periodFrom={periodFrom}
            periodTo={periodTo}
          />
        ))}
    </>
  );
};

const ProfitAndLossTable = ({
  tableData,
  setSelectedTransaction,
  highlightEntry,
  fromDate,
  toDate,
  periodFrom,
  periodTo,
}) => {
  const bgColor = useColorModeValue('white', 'gray.800');
  const [expandedSections, setExpandedSections] = useState({});
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: TableSortDirection.ascending,
  });
  // Keep track of the initially highlighted entry to maintain its position
  const [initialHighlightEntry, setInitialHighlightEntry] = useState(null);
  const {
    theme: { tokens },
  } = useNexus();

  const toggleSection = sectionName => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionName]: !prev[sectionName],
    }));
  };

  const handleSort = (key, newDirection) => {
    setSortConfig({ key, direction: newDirection });
  };

  const sortData = data => {
    if (!sortConfig.key) return data;

    const sortValue = item => {
      // Handle parent sections
      if (item.title) {
        if (sortConfig.key === 'currentYear')
          return parseValue(item.currentYear);
        if (sortConfig.key === 'previousYear')
          return parseValue(item.previousYear);
        if (sortConfig.key === 'change') return parseValue(item.change);
        if (sortConfig.key === 'changePercent')
          return parsePercentage(item.changePercent);
        if (sortConfig.key === 'name' || sortConfig.key === 'title') {
          return item.title;
        }
        return 0;
      }

      // Handle child items
      if (sortConfig.key === 'currentYear') return item._currentYear;
      if (sortConfig.key === 'previousYear') return item._previousYear;
      if (sortConfig.key === 'change') return item._change;
      if (sortConfig.key === 'changePercent')
        return parsePercentage(item.changePercent);
      if (sortConfig.key === 'name' || sortConfig.key === 'title') {
        return item.name;
      }
      return 0;
    };

    return [...data]
      .sort((a, b) => {
        const aValue = sortValue(a);
        const bValue = sortValue(b);

        if (aValue < bValue) {
          return sortConfig.direction === TableSortDirection.ascending ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === TableSortDirection.ascending ? 1 : -1;
        }
        return 0;
      })
      .map(section => {
        // Sort items within each section
        if (section.items && section.items.length > 0) {
          return {
            ...section,
            items: [...section.items].sort((a, b) => {
              const aValue = sortValue(a);
              const bValue = sortValue(b);

              if (aValue < bValue) {
                return sortConfig.direction === TableSortDirection.ascending
                  ? -1
                  : 1;
              }
              if (aValue > bValue) {
                return sortConfig.direction === TableSortDirection.ascending
                  ? 1
                  : -1;
              }
              return 0;
            }),
          };
        }
        return section;
      });
  };

  // State to track which section contains the highlighted entry
  const [highlightedParentSection, setHighlightedParentSection] =
    useState(null);

  // Effect to auto-expand sections when highlightEntry changes
  useEffect(() => {
    // Check if there's a section to toggle from the parent component
    if (window.lastSectionToToggle) {
      const sectionToToggle = window.lastSectionToToggle;
      // Clear the global variable to prevent toggling on subsequent renders
      window.lastSectionToToggle = null;

      // Toggle the section
      setExpandedSections(prev => ({
        ...prev,
        [sectionToToggle]: !prev[sectionToToggle],
      }));

      // Return early to prevent the rest of the effect from running
      return;
    }

    if (highlightEntry) {
      // Store the initial highlight entry to maintain its position even after highlight is removed
      setInitialHighlightEntry(highlightEntry);

      // Find the parent section for the highlighted entry
      const sections = transformData(tableData);

      // Create a new expanded sections object
      const newExpandedSections = { ...expandedSections };
      let foundParentSection = null;

      // Look through all sections to find which one contains the highlighted entry
      sections.forEach(section => {
        if (section.items && section.items.length > 0) {
          // Check if this section contains the highlighted entry directly
          const foundItem = section.items.find(
            item => item.name === highlightEntry
          );

          if (foundItem) {
            // Auto-expand this section
            newExpandedSections[section.title] = true;
            foundParentSection = section.title;
          }

          // Also check nested items (if any)
          section.items.forEach(subItem => {
            if (subItem.items && subItem.items.length > 0) {
              const foundInSubItem = subItem.items.find(
                nestedItem => nestedItem.name === highlightEntry
              );

              if (foundInSubItem) {
                // Expand both the parent section and the sub-section
                newExpandedSections[section.title] = true;
                newExpandedSections[subItem.name] = true;
                foundParentSection = section.title;
              }
            }
          });
        }
      });

      // Update expanded sections state
      setExpandedSections(newExpandedSections);

      // Store the parent section title
      if (foundParentSection) {
        setHighlightedParentSection(foundParentSection);
      }
    } else {
      // Don't clear the parent section when highlight is removed
      // This keeps the section expanded and visible
      // setHighlightedParentSection(null);
    }
  }, [highlightEntry, tableData, expandedSections, setExpandedSections]);

  // Function to reorder items and move highlighted item to the top
  const reorderWithHighlightedItemOnTop = sections => {
    // Use initialHighlightEntry instead of highlightEntry to maintain position
    // even after the highlight is removed
    const entryToHighlight = initialHighlightEntry;

    if (!entryToHighlight) return sections;

    return sections.map(section => {
      // If this section doesn't have items, return it as is
      if (!section.items || section.items.length === 0) {
        return section;
      }

      // Check if the highlighted item is directly in this section
      const highlightedItemIndex = section.items.findIndex(
        item => item.name === entryToHighlight
      );

      if (highlightedItemIndex !== -1) {
        // Found the highlighted item in this section
        const newItems = [...section.items];
        // Remove the highlighted item from its current position
        const highlightedItem = newItems.splice(highlightedItemIndex, 1)[0];
        // Add it to the beginning of the array
        newItems.unshift(highlightedItem);

        // Return the section with reordered items
        return {
          ...section,
          items: newItems,
        };
      }

      // Check if the highlighted item is in a nested subsection
      const newItems = section.items.map(subItem => {
        if (subItem.items && subItem.items.length > 0) {
          const nestedHighlightedItemIndex = subItem.items.findIndex(
            nestedItem => nestedItem.name === entryToHighlight
          );

          if (nestedHighlightedItemIndex !== -1) {
            // Found the highlighted item in this subsection
            const newNestedItems = [...subItem.items];
            // Remove the highlighted item from its current position
            const highlightedItem = newNestedItems.splice(
              nestedHighlightedItemIndex,
              1
            )[0];
            // Add it to the beginning of the array
            newNestedItems.unshift(highlightedItem);

            // Return the subsection with reordered items
            return {
              ...subItem,
              items: newNestedItems,
            };
          }
        }

        // No highlighted item in this subsection
        return subItem;
      });

      // Return the section with potentially modified subsections
      return {
        ...section,
        items: newItems,
      };
    });
  };

  const sections = transformData(tableData);
  // First reorder to move highlighted item to the top of its section
  const reorderedSections = reorderWithHighlightedItemOnTop(sections);
  // Then apply sorting while preserving the highlighted item at the top of its section
  const sortedSections = sortData(reorderedSections);

  return (
    <Box bg={bgColor} borderRadius="lg" overflow="hidden">
      <Table fontSize={tokens.fontSize100} fontWeight={600}>
        <TableHead>
          <TableRow variant="filled">
            <TableHeaderSortCell
              onSort={direction => handleSort('name', direction)}
              direction={
                sortConfig.key === 'name' ? sortConfig.direction : undefined
              }
            >
              Description
            </TableHeaderSortCell>
            <TableHeaderSortCell
              onSort={direction => handleSort('currentYear', direction)}
              direction={
                sortConfig.key === 'currentYear'
                  ? sortConfig.direction
                  : undefined
              }
              textAlign="right"
            >
              Current year ($)
            </TableHeaderSortCell>
            <TableHeaderSortCell
              onSort={direction => handleSort('previousYear', direction)}
              direction={
                sortConfig.key === 'previousYear'
                  ? sortConfig.direction
                  : undefined
              }
              textAlign="right"
            >
              Previous year ($)
            </TableHeaderSortCell>
            <TableHeaderSortCell
              onSort={direction => handleSort('change', direction)}
              direction={
                sortConfig.key === 'change' ? sortConfig.direction : undefined
              }
              textAlign="right"
            >
              Change ($)
            </TableHeaderSortCell>
            <TableHeaderSortCell
              onSort={direction => handleSort('changePercent', direction)}
              direction={
                sortConfig.key === 'changePercent'
                  ? sortConfig.direction
                  : undefined
              }
              textAlign="right"
            >
              Change (%)
            </TableHeaderSortCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedSections.map((section, index) => (
            <RecursiveTableRow
              key={index}
              item={section}
              level={0}
              expandedSections={expandedSections}
              toggleSection={toggleSection}
              setSelectedTransaction={setSelectedTransaction}
              highlightEntry={highlightEntry}
              highlightedParentSection={highlightedParentSection}
              fromDate={fromDate}
              toDate={toDate}
              periodFrom={periodFrom}
              periodTo={periodTo}
            />
          ))}
        </TableBody>
      </Table>
    </Box>
  );
};

export default ProfitAndLossTable;
